import React, { useEffect, useState, useMemo } from 'react';
import { supportApi, changeRequestApi } from '../services/api';
import { useToast } from '../context/ToastContext';
import {
  Plus, Send, ArrowLeft, Clock, Search, SortAsc, SortDesc,
  MessageSquare, AlertCircle, CheckCircle2, Timer, Inbox,
  BarChart3, ChevronRight, User, Tag, Calendar, Zap,
  ClipboardList, Check, X,
} from 'lucide-react';
import './Pages.css';

/* ============================================================
   Types
   ============================================================ */

interface Ticket {
  id: string;
  subject: string;
  message: string;
  status: string;
  priority: string;
  category: string | null;
  source: string;
  created_at: string;
  resolved_at: string | null;
  first_response_at: string | null;
  sla_due_at: string | null;
  sla_status: string | null;
  reply_count: number;
  client_name?: string | null;
}

interface Reply {
  id: string;
  sender_type: string;
  sender_name: string;
  message: string;
  is_internal: boolean;
  created_at: string;
}

interface TicketDetail extends Ticket {
  replies: Reply[];
}

/* ============================================================
   Constants & Mappings
   ============================================================ */

const statusLabels: Record<string, string> = {
  open: 'Offen', in_progress: 'In Bearbeitung', resolved: 'Gelöst',
};
const statusColors: Record<string, { bg: string; text: string; icon: string }> = {
  open: { bg: '#fef3c7', text: '#92400e', icon: '#f59e0b' },
  in_progress: { bg: '#dbeafe', text: '#1e40af', icon: '#3b82f6' },
  resolved: { bg: '#dcfce7', text: '#166534', icon: '#22c55e' },
};
const priorityLabels: Record<string, string> = {
  low: 'Niedrig', normal: 'Normal', high: 'Hoch', urgent: 'Dringend',
};
const priorityColors: Record<string, string> = {
  low: '#94a3b8', normal: '#64748b', high: '#f59e0b', urgent: '#ef4444',
};
const categoryLabels: Record<string, string> = {
  technisch: 'Technisch', abrechnung: 'Abrechnung', termine: 'Termine',
  konto: 'Konto', sonstiges: 'Sonstiges', feature: 'Feature-Wunsch',
};
const categoryOptions = ['technisch', 'abrechnung', 'termine', 'konto', 'sonstiges', 'feature'];

/* --- Change Request Types & Constants --- */

interface ChangeRequest {
  id: string;
  client_id: string;
  coach_id: string;
  status: string;
  field_name: string;
  old_value: string | null;
  new_value: string;
  reason: string | null;
  coach_comment: string | null;
  reviewed_at: string | null;
  created_at: string;
  client_name: string | null;
  client_email: string | null;
}

const FIELD_LABELS: Record<string, string> = {
  first_name: 'Vorname', last_name: 'Nachname', email: 'E-Mail', phone: 'Telefon',
  street: 'Straße', zip_code: 'PLZ', city: 'Ort', country: 'Land', address: 'Adresse',
};

function formatChangeValue(fieldName: string, val: string | null): string {
  if (!val) return '\u2014';
  if (fieldName === 'address') {
    try {
      const a = JSON.parse(val);
      return [a.street, [a.zip_code, a.city].filter(Boolean).join(' '), a.country].filter(Boolean).join(', ') || '\u2014';
    } catch { return val; }
  }
  return val;
}

const cannedResponses = [
  { label: 'Wird bearbeitet', text: 'Vielen Dank für Ihre Nachricht. Ich habe Ihr Anliegen erhalten und kümmere mich darum. Ich melde mich zeitnah mit einer Lösung.' },
  { label: 'Mehr Info nötig', text: 'Danke für Ihre Anfrage. Um Ihnen bestmöglich helfen zu können, benötige ich noch einige zusätzliche Informationen:\n\n- \n- \n\nBitte lassen Sie mir diese zukommen.' },
  { label: 'Problem gelöst', text: 'Das Problem wurde behoben. Bitte prüfen Sie, ob alles wie gewünscht funktioniert. Falls Sie weitere Fragen haben, stehe ich Ihnen gerne zur Verfügung.' },
  { label: 'Terminvorschlag', text: 'Gerne können wir das in einem kurzen Gespräch klären. Bitte buchen Sie einen freien Termin über meine Buchungsseite oder nennen Sie mir 2-3 Termine, die Ihnen passen.' },
];

type ViewMode = 'dashboard' | 'list' | 'create' | 'detail' | 'client-detail';
type SortField = 'created_at' | 'priority' | 'status';

/* ============================================================
   Helper Functions
   ============================================================ */

const fmtDateTime = (d: string) => new Date(d).toLocaleString('de-DE', {
  day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit',
});

const fmtDate = (d: string) => new Date(d).toLocaleDateString('de-DE', {
  day: '2-digit', month: '2-digit', year: 'numeric',
});

const timeAgo = (d: string) => {
  const diff = Date.now() - new Date(d).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'gerade eben';
  if (mins < 60) return `vor ${mins} Min.`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `vor ${hours} Std.`;
  const days = Math.floor(hours / 24);
  return `vor ${days} Tag${days > 1 ? 'en' : ''}`;
};

const priorityWeight: Record<string, number> = { urgent: 4, high: 3, normal: 2, low: 1 };

/* ============================================================
   Main Component
   ============================================================ */

export default function SupportPage() {
  const { showToast } = useToast();
  const [view, setView] = useState<ViewMode>('dashboard');
  const [tab, setTab] = useState<'own' | 'clients' | 'changes'>('own');
  const [tickets, setTickets] = useState<Ticket[]>([]);
  const [clientTickets, setClientTickets] = useState<Ticket[]>([]);
  const [changeRequests, setChangeRequests] = useState<ChangeRequest[]>([]);
  const [changeFilter, setChangeFilter] = useState<string>('pending');
  const [rejectChangeId, setRejectChangeId] = useState<string | null>(null);
  const [rejectChangeComment, setRejectChangeComment] = useState('');
  const [changeActionLoading, setChangeActionLoading] = useState<string | null>(null);
  const [selectedTicket, setSelectedTicket] = useState<TicketDetail | null>(null);
  const [statusFilter, setStatusFilter] = useState<string>('');
  const [searchQuery, setSearchQuery] = useState('');
  const [sortField, setSortField] = useState<SortField>('created_at');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc');
  const [loading, setLoading] = useState(false);

  // Create form
  const [subject, setSubject] = useState('');
  const [message, setMessage] = useState('');
  const [category, setCategory] = useState('sonstiges');
  const [priority, setPriority] = useState('normal');

  // Reply
  const [replyText, setReplyText] = useState('');
  const [replying, setReplying] = useState(false);
  const [showCanned, setShowCanned] = useState(false);

  /* --- Data Loading --- */

  const loadTickets = React.useCallback(async () => {
    try {
      const res = await supportApi.tickets();
      setTickets(res.data);
    } catch { /* empty */ }
  }, []);

  const loadClientTickets = React.useCallback(async () => {
    try {
      const res = await supportApi.clientTickets();
      setClientTickets(res.data);
    } catch { /* empty */ }
  }, []);

  const loadChangeRequests = React.useCallback(async () => {
    try {
      const res = await changeRequestApi.list(changeFilter ? { status: changeFilter } : undefined);
      setChangeRequests(res.data.items || []);
    } catch { /* empty */ }
  }, [changeFilter]);

  useEffect(() => { loadTickets(); loadClientTickets(); loadChangeRequests(); }, [loadTickets, loadClientTickets, loadChangeRequests]);

  /* --- Filtered + Sorted Tickets --- */

  const displayTickets = useMemo(() => {
    let list = tab === 'own' ? [...tickets] : [...clientTickets];

    // Status filter
    if (statusFilter) {
      if (statusFilter === 'open') {
        list = list.filter((t) => t.status === 'open' || t.status === 'in_progress');
      } else {
        list = list.filter((t) => t.status === statusFilter);
      }
    }

    // Search
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      list = list.filter((t) =>
        t.subject.toLowerCase().includes(q) ||
        t.message.toLowerCase().includes(q) ||
        (t.client_name && t.client_name.toLowerCase().includes(q)) ||
        (t.category && t.category.toLowerCase().includes(q))
      );
    }

    // Sort
    list.sort((a, b) => {
      let cmp = 0;
      if (sortField === 'created_at') {
        cmp = new Date(a.created_at).getTime() - new Date(b.created_at).getTime();
      } else if (sortField === 'priority') {
        cmp = (priorityWeight[a.priority] || 2) - (priorityWeight[b.priority] || 2);
      } else if (sortField === 'status') {
        const order: Record<string, number> = { open: 1, in_progress: 2, resolved: 3 };
        cmp = (order[a.status] || 0) - (order[b.status] || 0);
      }
      return sortDir === 'desc' ? -cmp : cmp;
    });

    return list;
  }, [tab, tickets, clientTickets, statusFilter, searchQuery, sortField, sortDir]);

  /* --- KPI Calculations --- */

  const allTickets = useMemo(() => [...tickets, ...clientTickets], [tickets, clientTickets]);

  const kpi = useMemo(() => {
    const total = allTickets.length;
    const open = allTickets.filter((t) => t.status === 'open').length;
    const inProgress = allTickets.filter((t) => t.status === 'in_progress').length;
    const resolved = allTickets.filter((t) => t.status === 'resolved').length;
    const breached = allTickets.filter((t) => t.sla_status === 'breached').length;
    const warning = allTickets.filter((t) => t.sla_status === 'warning').length;

    const responded = allTickets.filter((t) => t.first_response_at && t.created_at);
    let avgResponseH = 0;
    if (responded.length > 0) {
      const totalMs = responded.reduce((sum, t) =>
        sum + (new Date(t.first_response_at!).getTime() - new Date(t.created_at).getTime()), 0);
      avgResponseH = Math.round(totalMs / responded.length / 3600000 * 10) / 10;
    }

    const slaRate = total > 0 ? Math.round(((total - breached) / total) * 100) : 100;

    return { total, open, inProgress, resolved, breached, warning, avgResponseH, slaRate };
  }, [allTickets]);

  // Urgent tickets (breached SLA or high/urgent priority, still open)
  const urgentTickets = useMemo(() =>
    allTickets
      .filter((t) => t.status !== 'resolved' && (
        t.sla_status === 'breached' || t.sla_status === 'warning' ||
        t.priority === 'urgent' || t.priority === 'high'
      ))
      .sort((a, b) => {
        if (a.sla_status === 'breached' && b.sla_status !== 'breached') return -1;
        if (b.sla_status === 'breached' && a.sla_status !== 'breached') return 1;
        return (priorityWeight[b.priority] || 0) - (priorityWeight[a.priority] || 0);
      })
      .slice(0, 5),
    [allTickets]
  );

  /* --- Actions --- */

  const handleCreate = async () => {
    if (!subject.trim() || !message.trim()) {
      showToast('Betreff und Nachricht sind Pflichtfelder', 'info');
      return;
    }
    setLoading(true);
    try {
      await supportApi.createTicket({ subject, message, category, priority });
      showToast('Ticket erstellt!', 'success');
      setSubject(''); setMessage(''); setCategory('sonstiges'); setPriority('normal');
      setView('list');
      loadTickets();
    } catch (err: any) {
      showToast(err.response?.data?.detail || 'Fehler beim Erstellen', 'error');
    } finally { setLoading(false); }
  };

  const openDetail = async (id: string, isClient: boolean) => {
    try {
      const res = isClient
        ? await supportApi.clientTicketDetail(id)
        : await supportApi.ticketDetail(id);
      setSelectedTicket(res.data);
      setView(isClient ? 'client-detail' : 'detail');
      setReplyText('');
      setShowCanned(false);
    } catch { showToast('Ticket konnte nicht geladen werden', 'error'); }
  };

  const handleReply = async () => {
    if (!replyText.trim() || !selectedTicket) return;
    setReplying(true);
    try {
      const isClient = view === 'client-detail';
      if (isClient) {
        await supportApi.replyClientTicket(selectedTicket.id, { message: replyText });
      } else {
        await supportApi.replyTicket(selectedTicket.id, { message: replyText });
      }
      showToast('Antwort gesendet', 'success');
      setReplyText('');
      setShowCanned(false);
      openDetail(selectedTicket.id, isClient);
      loadTickets(); loadClientTickets();
    } catch (err: any) {
      showToast(err.response?.data?.detail || 'Fehler', 'error');
    } finally { setReplying(false); }
  };

  const handleStatusChange = async (ticketId: string, newStatus: string, isClient: boolean) => {
    try {
      if (isClient) {
        await supportApi.updateClientTicketStatus(ticketId, { status: newStatus });
      } else {
        await supportApi.updateTicketStatus(ticketId, { status: newStatus });
      }
      showToast(`Status geändert: ${statusLabels[newStatus] || newStatus}`, 'success');
      // Reload detail + lists
      openDetail(ticketId, isClient);
      loadTickets(); loadClientTickets();
    } catch (err: any) {
      showToast(err.response?.data?.detail || 'Fehler beim Statuswechsel', 'error');
    }
  };

  const handleApproveChange = async (id: string) => {
    setChangeActionLoading(id);
    try {
      await changeRequestApi.approve(id);
      showToast('Änderung genehmigt und übernommen', 'success');
      loadChangeRequests();
    } catch (err: any) {
      showToast(err?.response?.data?.detail || 'Fehler', 'error');
    } finally { setChangeActionLoading(null); }
  };

  const handleRejectChange = async (id: string) => {
    setChangeActionLoading(id);
    try {
      await changeRequestApi.reject(id, { comment: rejectChangeComment.trim() || undefined });
      showToast('Änderung abgelehnt', 'success');
      setRejectChangeId(null);
      setRejectChangeComment('');
      loadChangeRequests();
    } catch (err: any) {
      showToast(err?.response?.data?.detail || 'Fehler', 'error');
    } finally { setChangeActionLoading(null); }
  };

  const pendingChangesCount = changeRequests.filter(cr => cr.status === 'pending').length;

  const toggleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDir((d) => d === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDir('desc');
    }
  };

  /* --- SLA Helper --- */

  const SlaCountdown = ({ ticket }: { ticket: Ticket }) => {
    if (ticket.status === 'resolved' || ticket.first_response_at) return null;
    if (!ticket.sla_due_at) return null;
    const due = new Date(ticket.sla_due_at);
    const diffMs = due.getTime() - Date.now();
    const diffH = Math.floor(Math.abs(diffMs) / 3600000);
    const diffM = Math.floor((Math.abs(diffMs) % 3600000) / 60000);
    const isBreached = diffMs < 0;
    const isWarning = !isBreached && diffH < 6;
    const color = isBreached ? '#ef4444' : isWarning ? '#f59e0b' : '#22c55e';
    return (
      <span style={{
        padding: '3px 10px', borderRadius: 10, fontSize: '0.72rem', fontWeight: 600,
        background: `${color}12`, color, border: `1px solid ${color}30`,
        display: 'inline-flex', alignItems: 'center', gap: 4, whiteSpace: 'nowrap',
      }}>
        <Timer size={11} />
        {isBreached ? `Überfällig ${diffH}h ${diffM}m` : `${diffH}h ${diffM}m`}
      </span>
    );
  };

  /* ============================================================
     DETAIL VIEW
     ============================================================ */

  if (view === 'detail' || view === 'client-detail') {
    const t = selectedTicket;
    if (!t) return null;
    const isClient = view === 'client-detail';
    const sc = statusColors[t.status] || statusColors.open;

    return (
      <div className="page-content">
        <button className="btn btn-secondary" style={{ marginBottom: 16, display: 'flex', alignItems: 'center', gap: 6 }}
          onClick={() => { setView('list'); setTab(isClient ? 'clients' : 'own'); }}>
          <ArrowLeft size={16} /> Zurück zur Liste
        </button>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 300px', gap: 20, alignItems: 'start' }}>
          {/* Left: Conversation */}
          <div>
            <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
              {/* Header */}
              <div style={{ background: 'var(--sidebar-bg)', color: 'var(--sidebar-text)', padding: '16px 24px' }}>
                <div style={{ fontWeight: 600, fontSize: '1.05rem', marginBottom: 4 }}>{t.subject}</div>
                <div style={{ fontSize: '0.8rem', opacity: 0.8, display: 'flex', gap: 12, alignItems: 'center' }}>
                  <span>#{t.id.slice(0, 8)}</span>
                  <span>{fmtDateTime(t.created_at)}</span>
                  {isClient && t.client_name && <span>von {t.client_name}</span>}
                </div>
              </div>

              {/* Conversation Thread */}
              <div style={{ padding: '20px 24px' }}>
                {/* Original message */}
                <div style={{ marginBottom: 20 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10 }}>
                    <div style={{
                      width: 36, height: 36, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center',
                      background: isClient ? 'var(--primary)' : 'var(--primary)', color: 'white', fontWeight: 600, fontSize: '0.85rem',
                    }}>
                      {isClient ? (t.client_name?.[0] || 'K') : 'S'}
                    </div>
                    <div>
                      <div style={{ fontWeight: 600, fontSize: '0.9rem' }}>
                        {isClient ? (t.client_name || 'Klient') : 'Sie'}
                      </div>
                      <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>{timeAgo(t.created_at)}</div>
                    </div>
                  </div>
                  <div style={{
                    padding: 16, background: 'var(--bg)', borderRadius: '4px 12px 12px 12px',
                    border: '1px solid var(--border-light)', lineHeight: 1.6, whiteSpace: 'pre-wrap',
                  }}>
                    {t.message}
                  </div>
                </div>

                {/* Replies */}
                {t.replies.map((r) => {
                  const isMe = r.sender_type === 'coach';
                  const isAdmin = r.sender_type === 'admin';
                  return (
                    <div key={r.id} style={{
                      marginBottom: 20,
                      marginLeft: isMe ? 40 : 0,
                      marginRight: isMe ? 0 : 40,
                    }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10, justifyContent: isMe ? 'flex-end' : 'flex-start' }}>
                        {!isMe && (
                          <div style={{
                            width: 36, height: 36, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center',
                            background: isAdmin ? 'var(--primary)' : 'var(--primary)', color: 'white', fontWeight: 600, fontSize: '0.85rem',
                          }}>
                            {r.sender_name[0]}
                          </div>
                        )}
                        <div style={{ textAlign: isMe ? 'right' : 'left' }}>
                          <div style={{ fontWeight: 600, fontSize: '0.9rem', color: isAdmin ? 'var(--primary)' : 'inherit' }}>
                            {r.sender_name} {isAdmin && <span style={{ fontSize: '0.72rem', fontWeight: 400, color: 'var(--primary)' }}>(Admin)</span>}
                          </div>
                          <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>{timeAgo(r.created_at)}</div>
                        </div>
                        {isMe && (
                          <div style={{
                            width: 36, height: 36, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center',
                            background: 'var(--primary)', color: 'white', fontWeight: 600, fontSize: '0.85rem',
                          }}>S</div>
                        )}
                      </div>
                      <div style={{
                        padding: 16, lineHeight: 1.6, whiteSpace: 'pre-wrap',
                        background: isMe ? 'color-mix(in srgb, var(--primary) 10%, var(--bg-card))' : isAdmin ? 'color-mix(in srgb, var(--primary) 6%, var(--bg-card))' : 'var(--bg)',
                        borderRadius: isMe ? '12px 4px 12px 12px' : '4px 12px 12px 12px',
                        border: `1px solid ${isMe ? 'color-mix(in srgb, var(--primary) 25%, transparent)' : isAdmin ? 'color-mix(in srgb, var(--primary) 15%, transparent)' : 'var(--border-light)'}`,
                      }}>
                        {r.message}
                      </div>
                    </div>
                  );
                })}

                {/* Reply box */}
                {t.status !== 'resolved' && (
                  <div style={{ borderTop: '1px solid var(--border-light)', paddingTop: 20, marginTop: 8 }}>
                    {/* Canned responses */}
                    <div style={{ marginBottom: 10 }}>
                      <button
                        className="btn btn-secondary"
                        style={{ fontSize: '0.78rem', padding: '4px 12px' }}
                        onClick={() => setShowCanned(!showCanned)}
                      >
                        <Zap size={12} /> Schnellantworten
                      </button>
                      {showCanned && (
                        <div style={{
                          display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 8, padding: 12,
                          background: 'var(--bg)', borderRadius: 8, border: '1px solid var(--border-light)',
                        }}>
                          {cannedResponses.map((cr) => (
                            <button
                              key={cr.label}
                              className="chip"
                              style={{ fontSize: '0.75rem', padding: '4px 10px' }}
                              onClick={() => { setReplyText(cr.text); setShowCanned(false); }}
                            >
                              {cr.label}
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                    <textarea
                      placeholder="Ihre Antwort schreiben..."
                      value={replyText}
                      onChange={(e) => setReplyText(e.target.value)}
                      style={{
                        width: '100%', minHeight: 100, resize: 'vertical', borderRadius: 10,
                        border: '1px solid var(--border)', padding: 14, fontSize: '0.9rem',
                        marginBottom: 10, lineHeight: 1.6,
                      }}
                    />
                    <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
                      <button className="btn btn-primary" onClick={handleReply} disabled={replying || !replyText.trim()}>
                        <Send size={14} /> {replying ? 'Wird gesendet...' : 'Antwort senden'}
                      </button>
                    </div>
                  </div>
                )}

                {t.status === 'resolved' && (
                  <div style={{
                    textAlign: 'center', padding: 20, background: 'color-mix(in srgb, #22c55e 8%, var(--bg-card))', borderRadius: 10,
                    border: '1px dashed color-mix(in srgb, #22c55e 25%, transparent)', color: '#22c55e', fontSize: '0.88rem',
                  }}>
                    <CheckCircle2 size={20} style={{ marginBottom: 4 }} /><br />
                    Dieses Ticket wurde am {t.resolved_at ? fmtDate(t.resolved_at) : '–'} als gelöst markiert.
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Right: Sidebar */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            {/* Status + Workflow */}
            <div className="card" style={{ padding: 20 }}>
              <div style={{ fontSize: '0.75rem', fontWeight: 600, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 12 }}>
                Status
              </div>
              <span style={{
                display: 'inline-block', padding: '6px 16px', borderRadius: 20, fontSize: '0.85rem', fontWeight: 600,
                background: sc.bg, color: sc.text, marginBottom: 14,
              }}>
                {statusLabels[t.status] || t.status}
              </span>

              {/* Workflow Buttons */}
              <div style={{ fontSize: '0.75rem', fontWeight: 600, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 8, marginTop: 4 }}>
                Aktion
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                {t.status === 'open' && (
                  <>
                    <button className="btn btn-secondary" style={{ fontSize: '0.8rem', width: '100%', justifyContent: 'center' }}
                      onClick={() => handleStatusChange(t.id, 'in_progress', isClient)}>
                      <Clock size={13} /> In Bearbeitung setzen
                    </button>
                    <button className="btn" style={{ fontSize: '0.8rem', width: '100%', justifyContent: 'center', background: 'color-mix(in srgb, #22c55e 12%, var(--bg-card))', color: '#22c55e', border: '1px solid color-mix(in srgb, #22c55e 25%, transparent)' }}
                      onClick={() => handleStatusChange(t.id, 'resolved', isClient)}>
                      <CheckCircle2 size={13} /> Als gelöst markieren
                    </button>
                  </>
                )}
                {t.status === 'in_progress' && (
                  <>
                    <button className="btn" style={{ fontSize: '0.8rem', width: '100%', justifyContent: 'center', background: 'color-mix(in srgb, #22c55e 12%, var(--bg-card))', color: '#22c55e', border: '1px solid color-mix(in srgb, #22c55e 25%, transparent)' }}
                      onClick={() => handleStatusChange(t.id, 'resolved', isClient)}>
                      <CheckCircle2 size={13} /> Als gelöst markieren
                    </button>
                    <button className="btn btn-secondary" style={{ fontSize: '0.78rem', width: '100%', justifyContent: 'center' }}
                      onClick={() => handleStatusChange(t.id, 'open', isClient)}>
                      Zurück auf Offen
                    </button>
                  </>
                )}
                {t.status === 'resolved' && (
                  <button className="btn btn-secondary" style={{ fontSize: '0.8rem', width: '100%', justifyContent: 'center' }}
                    onClick={() => handleStatusChange(t.id, 'open', isClient)}>
                    <AlertCircle size={13} /> Wieder öffnen
                  </button>
                )}
              </div>
            </div>

            {/* Properties */}
            <div className="card" style={{ padding: 20 }}>
              <div style={{ fontSize: '0.75rem', fontWeight: 600, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 14 }}>
                Eigenschaften
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                  <Tag size={14} style={{ color: 'var(--text-muted)' }} />
                  <div>
                    <div style={{ fontSize: '0.72rem', color: 'var(--text-muted)' }}>Priorität</div>
                    <div style={{ fontSize: '0.88rem', fontWeight: 500, color: priorityColors[t.priority] || 'inherit' }}>
                      {priorityLabels[t.priority] || t.priority}
                    </div>
                  </div>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                  <Inbox size={14} style={{ color: 'var(--text-muted)' }} />
                  <div>
                    <div style={{ fontSize: '0.72rem', color: 'var(--text-muted)' }}>Kategorie</div>
                    <div style={{ fontSize: '0.88rem', fontWeight: 500 }}>
                      {categoryLabels[t.category || ''] || t.category || 'Sonstiges'}
                    </div>
                  </div>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                  <Calendar size={14} style={{ color: 'var(--text-muted)' }} />
                  <div>
                    <div style={{ fontSize: '0.72rem', color: 'var(--text-muted)' }}>Erstellt</div>
                    <div style={{ fontSize: '0.88rem' }}>{fmtDateTime(t.created_at)}</div>
                  </div>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                  <MessageSquare size={14} style={{ color: 'var(--text-muted)' }} />
                  <div>
                    <div style={{ fontSize: '0.72rem', color: 'var(--text-muted)' }}>Nachrichten</div>
                    <div style={{ fontSize: '0.88rem' }}>{(t.replies?.length || 0) + 1}</div>
                  </div>
                </div>
                {isClient && t.client_name && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                    <User size={14} style={{ color: 'var(--text-muted)' }} />
                    <div>
                      <div style={{ fontSize: '0.72rem', color: 'var(--text-muted)' }}>Klient</div>
                      <div style={{ fontSize: '0.88rem', fontWeight: 500 }}>{t.client_name}</div>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* SLA */}
            {t.sla_due_at && t.status !== 'resolved' && (
              <div className="card" style={{ padding: 20 }}>
                <div style={{ fontSize: '0.75rem', fontWeight: 600, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 12 }}>
                  SLA
                </div>
                <SlaCountdown ticket={t} />
                {t.first_response_at && (
                  <div style={{ marginTop: 10, fontSize: '0.82rem', color: '#22c55e', display: 'flex', alignItems: 'center', gap: 6 }}>
                    <CheckCircle2 size={14} /> Erste Antwort: {fmtDateTime(t.first_response_at)}
                  </div>
                )}
                <div style={{ marginTop: 10, fontSize: '0.78rem', color: 'var(--text-muted)' }}>
                  Fällig: {fmtDateTime(t.sla_due_at)}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }

  /* ============================================================
     CREATE VIEW
     ============================================================ */

  if (view === 'create') {
    return (
      <div className="page-content">
        <button className="btn btn-secondary" style={{ marginBottom: 16, display: 'flex', alignItems: 'center', gap: 6 }}
          onClick={() => setView('list')}>
          <ArrowLeft size={16} /> Zurück
        </button>
        <div className="card" style={{ padding: 0, overflow: 'hidden', maxWidth: 720 }}>
          <div style={{ background: 'var(--sidebar-bg)', color: 'var(--sidebar-text)', padding: '16px 24px' }}>
            <div style={{ fontWeight: 600, fontSize: '1.05rem' }}>Neues Support-Ticket</div>
            <div style={{ fontSize: '0.8rem', opacity: 0.75, marginTop: 2 }}>Beschreiben Sie Ihr Anliegen und wir kümmern uns darum.</div>
          </div>
          <div style={{ padding: 24 }}>
            <div style={{ marginBottom: 16 }}>
              <label style={{ fontWeight: 600, fontSize: '0.85rem', marginBottom: 8, display: 'block' }}>Kategorie</label>
              <div className="category-chips">
                {categoryOptions.map((c) => (
                  <button key={c} className={`chip ${category === c ? 'chip-active' : ''}`} onClick={() => setCategory(c)}>
                    {categoryLabels[c]}
                  </button>
                ))}
              </div>
            </div>
            <div className="form-group">
              <label>Betreff</label>
              <input value={subject} onChange={(e) => setSubject(e.target.value)} placeholder="Kurze Beschreibung des Problems..." />
            </div>
            <div className="form-group">
              <label>Nachricht</label>
              <textarea value={message} onChange={(e) => setMessage(e.target.value)} placeholder="Beschreiben Sie Ihr Anliegen ausführlich..."
                style={{ minHeight: 140, resize: 'vertical' }} />
            </div>
            <div style={{ marginBottom: 20 }}>
              <label style={{ fontWeight: 600, fontSize: '0.85rem', marginBottom: 8, display: 'block' }}>Priorität</label>
              <div className="category-chips">
                {(['low', 'normal', 'high', 'urgent'] as const).map((p) => (
                  <button key={p} className={`chip ${priority === p ? 'chip-active' : ''}`}
                    style={priority === p ? { borderColor: priorityColors[p], color: priorityColors[p] } : undefined}
                    onClick={() => setPriority(p)}>
                    {priorityLabels[p]}
                  </button>
                ))}
              </div>
            </div>
            <button className="btn btn-primary" onClick={handleCreate} disabled={loading} style={{ width: '100%', justifyContent: 'center' }}>
              {loading ? 'Wird erstellt...' : 'Ticket erstellen'}
            </button>
          </div>
        </div>
      </div>
    );
  }

  /* ============================================================
     DASHBOARD VIEW
     ============================================================ */

  if (view === 'dashboard') {
    return (
      <div className="page-content">
        {/* Header */}
        <div className="card" style={{ padding: 0, overflow: 'hidden', marginBottom: 24 }}>
          <div style={{
            background: 'var(--sidebar-bg)', color: 'var(--sidebar-text)', padding: '20px 24px',
            display: 'flex', justifyContent: 'space-between', alignItems: 'center',
          }}>
            <div>
              <div style={{ fontWeight: 600, fontSize: '1.15rem' }}>Support-Center</div>
              <div style={{ fontSize: '0.82rem', opacity: 0.75, marginTop: 2 }}>Übersicht über alle Tickets und Anfragen</div>
            </div>
            <div style={{ display: 'flex', gap: 10 }}>
              <button className="btn" onClick={() => setView('list')} style={{
                background: 'rgba(255,255,255,0.1)', color: 'var(--sidebar-text)', border: '1px solid rgba(255,255,255,0.2)',
                fontSize: '0.82rem', padding: '8px 16px',
              }}>
                <Inbox size={14} /> Alle Tickets
              </button>
              <button className="btn" onClick={() => setView('create')} style={{
                background: 'rgba(255,255,255,0.2)', color: 'var(--sidebar-text)', border: '1px solid rgba(255,255,255,0.3)',
                fontSize: '0.82rem', padding: '8px 16px',
              }}>
                <Plus size={14} /> Neues Ticket
              </button>
            </div>
          </div>
        </div>

        {/* KPI Cards */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: 16, marginBottom: 24 }}>
          <div className="card" style={{ padding: 20, cursor: 'pointer' }} onClick={() => { setView('list'); setStatusFilter(''); }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
              <div style={{ width: 44, height: 44, borderRadius: 10, background: '#B0746815', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <BarChart3 size={22} style={{ color: 'var(--primary)' }} />
              </div>
              <div>
                <div style={{ fontSize: '1.6rem', fontWeight: 700, color: 'var(--primary)', lineHeight: 1 }}>{kpi.total}</div>
                <div style={{ fontSize: '0.78rem', color: 'var(--text-muted)', marginTop: 2 }}>Gesamt</div>
              </div>
            </div>
          </div>

          <div className="card" style={{ padding: 20, cursor: 'pointer' }} onClick={() => { setView('list'); setStatusFilter('open'); }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
              <div style={{ width: 44, height: 44, borderRadius: 10, background: '#f59e0b15', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <AlertCircle size={22} style={{ color: '#f59e0b' }} />
              </div>
              <div>
                <div style={{ fontSize: '1.6rem', fontWeight: 700, color: '#f59e0b', lineHeight: 1 }}>{kpi.open + kpi.inProgress}</div>
                <div style={{ fontSize: '0.78rem', color: 'var(--text-muted)', marginTop: 2 }}>Offen</div>
              </div>
            </div>
          </div>

          <div className="card" style={{ padding: 20, cursor: 'pointer' }} onClick={() => { setView('list'); setStatusFilter('resolved'); }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
              <div style={{ width: 44, height: 44, borderRadius: 10, background: '#22c55e15', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <CheckCircle2 size={22} style={{ color: '#22c55e' }} />
              </div>
              <div>
                <div style={{ fontSize: '1.6rem', fontWeight: 700, color: '#22c55e', lineHeight: 1 }}>{kpi.resolved}</div>
                <div style={{ fontSize: '0.78rem', color: 'var(--text-muted)', marginTop: 2 }}>Gelöst</div>
              </div>
            </div>
          </div>

          <div className="card" style={{ padding: 20 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
              <div style={{ width: 44, height: 44, borderRadius: 10, background: '#3b82f615', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <Clock size={22} style={{ color: '#3b82f6' }} />
              </div>
              <div>
                <div style={{ fontSize: '1.6rem', fontWeight: 700, color: '#3b82f6', lineHeight: 1 }}>
                  {kpi.avgResponseH > 0 ? `${kpi.avgResponseH}h` : '–'}
                </div>
                <div style={{ fontSize: '0.78rem', color: 'var(--text-muted)', marginTop: 2 }}>Ø Antwortzeit</div>
              </div>
            </div>
          </div>

          <div className="card" style={{ padding: 20 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
              <div style={{ width: 44, height: 44, borderRadius: 10, background: kpi.slaRate >= 90 ? '#22c55e15' : '#ef444415', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <Timer size={22} style={{ color: kpi.slaRate >= 90 ? '#22c55e' : '#ef4444' }} />
              </div>
              <div>
                <div style={{ fontSize: '1.6rem', fontWeight: 700, color: kpi.slaRate >= 90 ? '#22c55e' : '#ef4444', lineHeight: 1 }}>{kpi.slaRate}%</div>
                <div style={{ fontSize: '0.78rem', color: 'var(--text-muted)', marginTop: 2 }}>SLA-Quote</div>
              </div>
            </div>
          </div>
        </div>

        {/* Two columns: Status Distribution + Urgent Tickets */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20, marginBottom: 24 }}>
          {/* Status Distribution */}
          <div className="card" style={{ padding: 24 }}>
            <div style={{ fontWeight: 600, marginBottom: 16, display: 'flex', alignItems: 'center', gap: 8 }}>
              <BarChart3 size={16} style={{ color: 'var(--primary)' }} /> Ticket-Status
            </div>
            {kpi.total === 0 ? (
              <div style={{ color: 'var(--text-muted)', fontSize: '0.88rem' }}>Noch keine Tickets vorhanden.</div>
            ) : (
              <>
                {/* Visual bar */}
                <div style={{ display: 'flex', height: 12, borderRadius: 6, overflow: 'hidden', marginBottom: 16 }}>
                  {kpi.open > 0 && <div style={{ flex: kpi.open, background: '#f59e0b' }} />}
                  {kpi.inProgress > 0 && <div style={{ flex: kpi.inProgress, background: '#3b82f6' }} />}
                  {kpi.resolved > 0 && <div style={{ flex: kpi.resolved, background: '#22c55e' }} />}
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                  {[
                    { label: 'Offen', count: kpi.open, color: '#f59e0b' },
                    { label: 'In Bearbeitung', count: kpi.inProgress, color: '#3b82f6' },
                    { label: 'Gelöst', count: kpi.resolved, color: '#22c55e' },
                  ].map((s) => (
                    <div key={s.label} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <div style={{ width: 10, height: 10, borderRadius: 3, background: s.color }} />
                        <span style={{ fontSize: '0.88rem' }}>{s.label}</span>
                      </div>
                      <span style={{ fontWeight: 600, fontSize: '0.88rem' }}>{s.count}</span>
                    </div>
                  ))}
                </div>
              </>
            )}

            {/* SLA mini section */}
            {kpi.breached > 0 && (
              <div style={{ marginTop: 16, padding: 12, background: '#fef2f2', borderRadius: 8, border: '1px solid #fecaca' }}>
                <div style={{ fontSize: '0.82rem', fontWeight: 600, color: '#991b1b', display: 'flex', alignItems: 'center', gap: 6 }}>
                  <AlertCircle size={14} /> {kpi.breached} Ticket{kpi.breached > 1 ? 's' : ''} mit überschrittener SLA
                </div>
              </div>
            )}
          </div>

          {/* Urgent / Attention Needed */}
          <div className="card" style={{ padding: 24 }}>
            <div style={{ fontWeight: 600, marginBottom: 16, display: 'flex', alignItems: 'center', gap: 8 }}>
              <Zap size={16} style={{ color: '#f59e0b' }} /> Braucht Aufmerksamkeit
            </div>
            {urgentTickets.length === 0 ? (
              <div style={{ textAlign: 'center', padding: 20, color: 'var(--text-muted)' }}>
                <CheckCircle2 size={32} style={{ color: '#22c55e', marginBottom: 8 }} />
                <div style={{ fontSize: '0.88rem' }}>Alles im grünen Bereich!</div>
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {urgentTickets.map((t) => (
                  <div
                    key={t.id}
                    style={{
                      padding: '10px 14px', borderRadius: 8, cursor: 'pointer',
                      background: 'var(--bg)', border: '1px solid var(--border-light)',
                      borderLeft: `4px solid ${t.sla_status === 'breached' ? '#ef4444' : priorityColors[t.priority] || '#f59e0b'}`,
                      transition: 'background 0.15s',
                    }}
                    onClick={() => openDetail(t.id, t.source === 'client')}
                    onMouseEnter={(e) => { e.currentTarget.style.background = 'var(--bg-card)'; }}
                    onMouseLeave={(e) => { e.currentTarget.style.background = 'var(--bg)'; }}
                  >
                    <div style={{ fontWeight: 600, fontSize: '0.85rem', marginBottom: 2 }}>{t.subject}</div>
                    <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                      <span style={{ fontSize: '0.72rem', color: 'var(--text-muted)' }}>{timeAgo(t.created_at)}</span>
                      <SlaCountdown ticket={t} />
                      {(t.priority === 'urgent' || t.priority === 'high') && (
                        <span style={{
                          fontSize: '0.68rem', fontWeight: 600, padding: '1px 8px', borderRadius: 8,
                          color: priorityColors[t.priority], border: `1px solid ${priorityColors[t.priority]}40`,
                          background: `${priorityColors[t.priority]}10`,
                        }}>
                          {priorityLabels[t.priority]}
                        </span>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Quick Links */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 16 }}>
          <div className="card" style={{ padding: 20, cursor: 'pointer', transition: 'box-shadow 0.15s' }}
            onClick={() => { setView('list'); setStatusFilter(''); }}
            onMouseEnter={(e) => { e.currentTarget.style.boxShadow = 'var(--shadow-md)'; }}
            onMouseLeave={(e) => { e.currentTarget.style.boxShadow = ''; }}
          >
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <div>
                <div style={{ fontWeight: 600, fontSize: '0.92rem', marginBottom: 4 }}>Meine Tickets</div>
                <div style={{ fontSize: '0.82rem', color: 'var(--text-muted)' }}>{tickets.length} Tickets</div>
              </div>
              <ChevronRight size={20} style={{ color: 'var(--text-muted)' }} />
            </div>
          </div>
          <div className="card" style={{ padding: 20, cursor: 'pointer', transition: 'box-shadow 0.15s' }}
            onClick={() => { setView('list'); setTab('clients'); setStatusFilter(''); }}
            onMouseEnter={(e) => { e.currentTarget.style.boxShadow = 'var(--shadow-md)'; }}
            onMouseLeave={(e) => { e.currentTarget.style.boxShadow = ''; }}
          >
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <div>
                <div style={{ fontWeight: 600, fontSize: '0.92rem', marginBottom: 4 }}>Klienten-Anfragen</div>
                <div style={{ fontSize: '0.82rem', color: 'var(--text-muted)' }}>{clientTickets.length} Anfragen</div>
              </div>
              <ChevronRight size={20} style={{ color: 'var(--text-muted)' }} />
            </div>
          </div>
          <div className="card" style={{ padding: 20, cursor: 'pointer', transition: 'box-shadow 0.15s', borderLeft: pendingChangesCount > 0 ? '4px solid #f59e0b' : undefined }}
            onClick={() => { setView('list'); setTab('changes'); }}
            onMouseEnter={(e) => { e.currentTarget.style.boxShadow = 'var(--shadow-md)'; }}
            onMouseLeave={(e) => { e.currentTarget.style.boxShadow = ''; }}
          >
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <div>
                <div style={{ fontWeight: 600, fontSize: '0.92rem', marginBottom: 4 }}>Änderungsanträge</div>
                <div style={{ fontSize: '0.82rem', color: 'var(--text-muted)' }}>
                  {pendingChangesCount > 0 ? `${pendingChangesCount} offen` : 'Keine offenen'}
                </div>
              </div>
              <ClipboardList size={20} style={{ color: pendingChangesCount > 0 ? '#f59e0b' : 'var(--text-muted)' }} />
            </div>
          </div>
          <div className="card" style={{ padding: 20, cursor: 'pointer', transition: 'box-shadow 0.15s', borderLeft: '4px solid #22c55e' }}
            onClick={() => { setView('list'); setStatusFilter('resolved'); }}
            onMouseEnter={(e) => { e.currentTarget.style.boxShadow = 'var(--shadow-md)'; }}
            onMouseLeave={(e) => { e.currentTarget.style.boxShadow = ''; }}
          >
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <div>
                <div style={{ fontWeight: 600, fontSize: '0.92rem', marginBottom: 4 }}>Gelöste Tickets</div>
                <div style={{ fontSize: '0.82rem', color: 'var(--text-muted)' }}>{kpi.resolved} abgeschlossen</div>
              </div>
              <CheckCircle2 size={20} style={{ color: '#22c55e' }} />
            </div>
          </div>
          <div className="card" style={{ padding: 20, cursor: 'pointer', transition: 'box-shadow 0.15s' }}
            onClick={() => setView('create')}
            onMouseEnter={(e) => { e.currentTarget.style.boxShadow = 'var(--shadow-md)'; }}
            onMouseLeave={(e) => { e.currentTarget.style.boxShadow = ''; }}
          >
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <div>
                <div style={{ fontWeight: 600, fontSize: '0.92rem', marginBottom: 4 }}>Neues Ticket</div>
                <div style={{ fontSize: '0.82rem', color: 'var(--text-muted)' }}>Support-Anfrage erstellen</div>
              </div>
              <Plus size={20} style={{ color: 'var(--primary)' }} />
            </div>
          </div>
        </div>
      </div>
    );
  }

  /* ============================================================
     LIST VIEW (Inbox)
     ============================================================ */

  const counts = {
    all: (tab === 'own' ? tickets : clientTickets).length,
    open: (tab === 'own' ? tickets : clientTickets).filter((t) => t.status === 'open' || t.status === 'in_progress').length,
    resolved: (tab === 'own' ? tickets : clientTickets).filter((t) => t.status === 'resolved').length,
  };

  return (
    <div className="page-content">
      {/* Header */}
      <div className="card" style={{ padding: 0, overflow: 'hidden', marginBottom: 20 }}>
        <div style={{
          background: 'var(--sidebar-bg)', color: 'var(--sidebar-text)', padding: '16px 24px',
          display: 'flex', justifyContent: 'space-between', alignItems: 'center',
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <button className="btn" onClick={() => setView('dashboard')} style={{
              background: 'rgba(255,255,255,0.1)', color: 'var(--sidebar-text)', border: '1px solid rgba(255,255,255,0.2)',
              fontSize: '0.82rem', padding: '6px 12px',
            }}>
              <ArrowLeft size={14} />
            </button>
            <div style={{ fontWeight: 600, fontSize: '1.05rem' }}>Ticket-Inbox</div>
          </div>
          <button className="btn" onClick={() => setView('create')} style={{
            background: 'rgba(255,255,255,0.2)', color: 'var(--sidebar-text)', border: '1px solid rgba(255,255,255,0.3)',
            fontSize: '0.82rem', padding: '8px 16px',
          }}>
            <Plus size={14} /> Neues Ticket
          </button>
        </div>

        <div style={{ padding: '16px 24px' }}>
          {/* Tabs */}
          <div className="category-chips" style={{ marginBottom: 12 }}>
            <button className={`chip ${tab === 'own' ? 'chip-active' : ''}`} onClick={() => setTab('own')}>
              Meine Tickets ({tickets.length})
            </button>
            <button className={`chip ${tab === 'clients' ? 'chip-active' : ''}`} onClick={() => setTab('clients')}>
              Klienten-Anfragen ({clientTickets.length})
            </button>
            <button className={`chip ${tab === 'changes' ? 'chip-active' : ''}`} onClick={() => setTab('changes')}>
              Änderungsanträge {pendingChangesCount > 0 && `(${pendingChangesCount})`}
            </button>
          </div>

          {/* Search + Filters + Sort (nur für Tickets) */}
          {tab !== 'changes' && (
            <div style={{ display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap' }}>
              <div style={{ position: 'relative', flex: 1, minWidth: 200 }}>
                <Search size={16} style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', color: 'var(--text-muted)', pointerEvents: 'none' }} />
                <input
                  type="text"
                  placeholder="Tickets durchsuchen..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  style={{ paddingLeft: 38, width: '100%', fontSize: '0.85rem' }}
                />
              </div>
              <div className="category-chips">
                <button className={`chip ${!statusFilter ? 'chip-active' : ''}`} onClick={() => setStatusFilter('')}>
                  Alle ({counts.all})
                </button>
                <button className={`chip ${statusFilter === 'open' ? 'chip-active' : ''}`} onClick={() => setStatusFilter('open')}>
                  Offen ({counts.open})
                </button>
                <button className={`chip ${statusFilter === 'resolved' ? 'chip-active' : ''}`} onClick={() => setStatusFilter('resolved')}>
                  Gelöst ({counts.resolved})
                </button>
              </div>
              <div style={{ display: 'flex', gap: 4 }}>
                {([
                  { field: 'created_at' as SortField, label: 'Datum' },
                  { field: 'priority' as SortField, label: 'Priorität' },
                ]).map((s) => (
                  <button
                    key={s.field}
                    className="chip"
                    style={sortField === s.field ? { background: 'var(--primary)', color: 'white', borderColor: 'var(--primary)' } : undefined}
                    onClick={() => toggleSort(s.field)}
                  >
                    {s.label}
                    {sortField === s.field && (sortDir === 'desc' ? <SortDesc size={12} /> : <SortAsc size={12} />)}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Filter für Änderungsanträge */}
          {tab === 'changes' && (
            <div className="category-chips">
              {[
                { key: 'pending', label: 'Offen' },
                { key: 'approved', label: 'Genehmigt' },
                { key: 'rejected', label: 'Abgelehnt' },
                { key: '', label: 'Alle' },
              ].map(f => (
                <button key={f.key} className={`chip ${changeFilter === f.key ? 'chip-active' : ''}`} onClick={() => setChangeFilter(f.key)}>
                  {f.label}
                </button>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Ticket List (nur bei own/clients) */}
      {tab !== 'changes' && (
        <>
          <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
            {displayTickets.length === 0 ? (
              <div style={{ padding: 48, textAlign: 'center', color: 'var(--text-muted)' }}>
                <Inbox size={40} style={{ marginBottom: 12, opacity: 0.4 }} />
                <div style={{ fontSize: '0.92rem', marginBottom: 4 }}>
                  {searchQuery ? 'Keine Tickets gefunden.' : (tab === 'own' ? 'Keine Support-Tickets vorhanden.' : 'Keine Klienten-Anfragen vorhanden.')}
                </div>
                {!searchQuery && (
                  <button className="btn btn-primary" style={{ marginTop: 12, fontSize: '0.82rem' }} onClick={() => setView('create')}>
                    <Plus size={14} /> Erstes Ticket erstellen
                  </button>
                )}
              </div>
            ) : (
              displayTickets.map((t, idx) => {
                const sc = statusColors[t.status] || statusColors.open;
                const pColor = priorityColors[t.priority] || '#64748b';
                const isUnresponded = !t.first_response_at && t.status !== 'resolved' && t.source === 'client';
                return (
                  <div key={t.id} style={{
                    padding: '14px 20px', cursor: 'pointer', transition: 'background 0.15s',
                    background: isUnresponded ? '#fffbeb' : 'var(--bg-card)',
                    borderBottom: idx < displayTickets.length - 1 ? '1px solid var(--border-light)' : 'none',
                    borderLeft: `4px solid ${pColor}`,
                  }}
                    onClick={() => openDetail(t.id, tab === 'clients')}
                    onMouseEnter={(e) => { e.currentTarget.style.background = 'var(--bg)'; }}
                    onMouseLeave={(e) => { e.currentTarget.style.background = isUnresponded ? '#fffbeb' : 'var(--bg-card)'; }}
                  >
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 3 }}>
                          <div style={{ fontWeight: 600, fontSize: '0.92rem' }}>{t.subject}</div>
                          {isUnresponded && (
                            <span style={{
                              fontSize: '0.65rem', fontWeight: 700, padding: '1px 6px', borderRadius: 4,
                              background: '#f59e0b', color: 'white', textTransform: 'uppercase', letterSpacing: '0.3px',
                            }}>
                              NEU
                            </span>
                          )}
                        </div>
                        <div style={{ fontSize: '0.82rem', color: 'var(--text-muted)', marginBottom: 6, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {t.message.length > 120 ? t.message.substring(0, 120) + '...' : t.message}
                        </div>
                        <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap', fontSize: '0.78rem', color: 'var(--text-muted)' }}>
                          {t.category && (
                            <span style={{ display: 'flex', alignItems: 'center', gap: 3 }}>
                              <Tag size={11} /> {categoryLabels[t.category] || t.category}
                            </span>
                          )}
                          {tab === 'clients' && t.client_name && (
                            <span style={{ display: 'flex', alignItems: 'center', gap: 3, fontWeight: 500 }}>
                              <User size={11} /> {t.client_name}
                            </span>
                          )}
                          <span style={{ display: 'flex', alignItems: 'center', gap: 3 }}>
                            <Clock size={11} /> {timeAgo(t.created_at)}
                          </span>
                          {t.reply_count > 0 && (
                            <span style={{ display: 'flex', alignItems: 'center', gap: 3 }}>
                              <MessageSquare size={11} /> {t.reply_count}
                            </span>
                          )}
                          <SlaCountdown ticket={t} />
                        </div>
                      </div>
                      <div style={{ display: 'flex', gap: 6, alignItems: 'center', flexShrink: 0, marginLeft: 16 }}>
                        <span style={{
                          padding: '4px 12px', borderRadius: 12, fontSize: '0.76rem', fontWeight: 600,
                          background: sc.bg, color: sc.text,
                        }}>
                          {statusLabels[t.status] || t.status}
                        </span>
                        {t.priority !== 'normal' && (
                          <span style={{
                            padding: '4px 10px', borderRadius: 12, fontSize: '0.76rem', fontWeight: 600,
                            border: `1.5px solid ${pColor}`, color: pColor,
                          }}>
                            {priorityLabels[t.priority] || t.priority}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })
            )}
          </div>
          {displayTickets.length > 0 && (
            <div style={{ textAlign: 'center', padding: '12px 0', fontSize: '0.78rem', color: 'var(--text-muted)' }}>
              {displayTickets.length} Ticket{displayTickets.length !== 1 ? 's' : ''} angezeigt
            </div>
          )}
        </>
      )}

      {/* Änderungsanträge */}
      {tab === 'changes' && (
        changeRequests.length === 0 ? (
          <div className="card" style={{ textAlign: 'center', padding: 48 }}>
            <ClipboardList size={40} style={{ color: 'var(--text-muted)', opacity: 0.4, marginBottom: 12 }} />
            <div style={{ color: 'var(--text-muted)', fontSize: '0.92rem' }}>
              {changeFilter === 'pending' ? 'Keine offenen Änderungsanträge' : 'Keine Anträge gefunden'}
            </div>
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            {changeRequests.map(cr => (
              <div key={cr.id} className="card" style={{ padding: 0, overflow: 'hidden' }}>
                <div style={{ padding: '14px 20px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: '1px solid var(--border-light)' }}>
                  <div>
                    <strong style={{ fontSize: '0.95rem' }}>{cr.client_name || 'Unbekannt'}</strong>
                    <span style={{ margin: '0 8px', color: '#ccc' }}>|</span>
                    <span style={{ color: '#666' }}>{FIELD_LABELS[cr.field_name] || cr.field_name}</span>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    {cr.status === 'pending' && (
                      <span style={{ fontSize: '0.75rem', padding: '3px 10px', borderRadius: 12, background: '#fef3e6', color: '#e67e22' }}>
                        <Clock size={12} style={{ marginRight: 4, verticalAlign: 'middle' }} />Offen
                      </span>
                    )}
                    {cr.status === 'approved' && (
                      <span style={{ fontSize: '0.75rem', padding: '3px 10px', borderRadius: 12, background: '#e8f5e9', color: '#2e7d32' }}>
                        <Check size={12} style={{ marginRight: 4, verticalAlign: 'middle' }} />Genehmigt
                      </span>
                    )}
                    {cr.status === 'rejected' && (
                      <span style={{ fontSize: '0.75rem', padding: '3px 10px', borderRadius: 12, background: '#fdecea', color: '#c62828' }}>
                        <X size={12} style={{ marginRight: 4, verticalAlign: 'middle' }} />Abgelehnt
                      </span>
                    )}
                  </div>
                </div>
                <div style={{ padding: '14px 20px' }}>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: cr.reason ? 12 : 0 }}>
                    <div>
                      <div style={{ fontSize: '0.75rem', color: '#999', marginBottom: 4, textTransform: 'uppercase', letterSpacing: '0.5px' }}>Aktuell</div>
                      <div style={{ padding: '10px 14px', background: '#f8f8f8', borderRadius: 8, border: '1px solid #eee', fontSize: '0.9rem' }}>
                        {formatChangeValue(cr.field_name, cr.old_value)}
                      </div>
                    </div>
                    <div>
                      <div style={{ fontSize: '0.75rem', color: '#999', marginBottom: 4, textTransform: 'uppercase', letterSpacing: '0.5px' }}>Neu</div>
                      <div style={{ padding: '10px 14px', background: '#e8f5e9', borderRadius: 8, border: '1px solid #c8e6c9', fontSize: '0.9rem', fontWeight: 600 }}>
                        {formatChangeValue(cr.field_name, cr.new_value)}
                      </div>
                    </div>
                  </div>
                  {cr.reason && (
                    <div style={{ fontSize: '0.85rem', color: '#666', marginTop: 8 }}>
                      <strong>Begründung:</strong> {cr.reason}
                    </div>
                  )}
                  {cr.coach_comment && (
                    <div style={{ fontSize: '0.85rem', color: '#c62828', marginTop: 4 }}>
                      <strong>Kommentar:</strong> {cr.coach_comment}
                    </div>
                  )}
                  <div style={{ fontSize: '0.75rem', color: '#aaa', marginTop: 8 }}>
                    Eingereicht: {fmtDateTime(cr.created_at)}
                    {cr.reviewed_at && <span> | Bearbeitet: {fmtDateTime(cr.reviewed_at)}</span>}
                  </div>
                </div>
                {cr.status === 'pending' && (
                  <div style={{ padding: '12px 20px', borderTop: '1px solid var(--border-light)', background: '#fafafa', display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
                    {rejectChangeId === cr.id ? (
                      <div style={{ display: 'flex', gap: 8, alignItems: 'center', flex: 1 }}>
                        <input
                          type="text"
                          placeholder="Kommentar (optional)"
                          value={rejectChangeComment}
                          onChange={e => setRejectChangeComment(e.target.value)}
                          style={{ flex: 1, fontSize: '0.85rem' }}
                          autoFocus
                        />
                        <button className="btn btn-secondary" style={{ fontSize: '0.8rem', padding: '6px 12px' }}
                          onClick={() => { setRejectChangeId(null); setRejectChangeComment(''); }}
                          disabled={changeActionLoading === cr.id}>
                          Abbrechen
                        </button>
                        <button className="btn" style={{ fontSize: '0.8rem', padding: '6px 12px', background: '#c62828', color: '#fff', border: 'none' }}
                          disabled={changeActionLoading === cr.id}
                          onClick={() => handleRejectChange(cr.id)}>
                          {changeActionLoading === cr.id ? '...' : 'Ablehnen'}
                        </button>
                      </div>
                    ) : (
                      <>
                        <button className="btn" style={{ fontSize: '0.8rem', padding: '6px 14px', background: '#c62828', color: '#fff', border: 'none' }}
                          disabled={changeActionLoading === cr.id}
                          onClick={() => setRejectChangeId(cr.id)}>
                          <X size={14} style={{ marginRight: 4, verticalAlign: 'middle' }} /> Ablehnen
                        </button>
                        <button className="btn btn-primary" style={{ fontSize: '0.8rem', padding: '6px 14px' }}
                          disabled={changeActionLoading === cr.id}
                          onClick={() => handleApproveChange(cr.id)}>
                          <Check size={14} style={{ marginRight: 4, verticalAlign: 'middle' }} />
                          {changeActionLoading === cr.id ? 'Wird übernommen...' : 'Genehmigen'}
                        </button>
                      </>
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>
        )
      )}
    </div>
  );
}
